import React, { useState, useEffect, useMemo } from 'react';
import { resolveGeometryLocation, ResolvedLocation } from '../../utils/locationResolver';
import { PinIcon } from './Icons';

interface GeometryLabelProps {
  geometry?: string; // GeoJSON string
  drawingType?: 'POINT' | 'LINE' | 'POLYGON';
  icon?: React.ReactNode;
  fallback?: string;
  full?: boolean; // show full detailed label
  showLevel?: boolean; // show the address level in parentheses
  as?: React.ElementType;
  style?: React.CSSProperties;
  className?: string;
  initialLabel?: string | null;
  initialFullLabel?: string | null;
}

const GeometryLabel: React.FC<GeometryLabelProps> = ({
  geometry,
  drawingType,
  icon,
  fallback = 'Unknown Location',
  full = false,
  showLevel = false,
  as = 'span',
  style,
  className,
  initialLabel,
  initialFullLabel
}) => {
  const hasInitialLabel = Boolean(initialLabel && initialLabel.trim().length > 0);
  const [resolvedLocation, setResolvedLocation] = useState<ResolvedLocation | null>(() => {
    if (!hasInitialLabel) return null;
    const trimmed = initialLabel!.trim();
    const trimmedFull = initialFullLabel?.trim();
    return {
      label: trimmed,
      fullLabel: trimmedFull || trimmed,
      level: 'unknown',
      components: {}
    };
  });
  const [isLoading, setIsLoading] = useState(!hasInitialLabel);
  const [error, setError] = useState<string | null>(null);

  // Memoize geometry parsing to avoid unnecessary recalculations
  const geometryObject = useMemo(() => {
    if (!geometry) return null;
    try {
      return JSON.parse(geometry);
    } catch {
      return null;
    }
  }, [geometry]);

  // Effect to resolve location when geometry changes
  useEffect(() => {
    if (hasInitialLabel) {
      const trimmed = initialLabel!.trim();
      const trimmedFull = initialFullLabel?.trim();
      setResolvedLocation({
        label: trimmed,
        fullLabel: trimmedFull || trimmed,
        level: 'unknown',
        components: {}
      });
      setIsLoading(false);
      setError(null);
      return;
    }

    if (!geometry || !geometryObject) {
      setResolvedLocation(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let mounted = true;
    const controller = new AbortController();

    const resolveLocation = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await resolveGeometryLocation(geometry, controller.signal);

        if (mounted) {
          setResolvedLocation(result);
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted && !controller.signal.aborted) {
          setError('Failed to resolve location');
          setIsLoading(false);
        }
      }
    };

    resolveLocation();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [geometry, geometryObject, hasInitialLabel, initialLabel, initialFullLabel]);

  // Determine the display text
  const getDisplayText = (): string => {
    if (error) return fallback;
    if (isLoading) return 'Resolving location...';
    if (!resolvedLocation) return fallback;

    const baseLabel = full ? resolvedLocation.fullLabel : resolvedLocation.label;

    if (showLevel && resolvedLocation.level !== 'unknown') {
      const levelDisplay = resolvedLocation.level.charAt(0).toUpperCase() + resolvedLocation.level.slice(1);
      return `${baseLabel} (${levelDisplay})`;
    }

    return baseLabel;
  };

  const Tag = as as any;
  const displayText = getDisplayText();
  const iconNode = icon !== undefined ? icon : <PinIcon size={12} />;
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    ...style
  };

  return (
    <Tag
      data-geometry-type={drawingType}
      style={baseStyle}
      className={className}
      title={full ? undefined : resolvedLocation?.fullLabel}
    >
      {iconNode}
      <span>{displayText}</span>
    </Tag>
  );
};

export default GeometryLabel;
